import * as history from '@dreamworld/web-util/history.js';
import URI from '@dw/urijs-esm';
import isDeviceReady from './device-ready.js';
import { hasDialogUnderTop, topOpenedDialog } from './components/dialog-history-manager.js';
import * as aiChat from './redux/ai-chat';
import * as router from './redux/router';
import { store } from './store.js';

/**
 * @returns {Boolean} `true` when the open layer pushed its own history entry: the current entry only adds a param (e.g. `?aichat`, `?attachment-action`) to the previous entry of the same page.
 */
const hasLayerHistoryEntry = () => {
  const historyList = history.getHistoryList();
  const currentIndex = history.getCurrentPageIndex();
  const previousUrl = historyList[currentIndex - 1];
  if (!previousUrl) {
    return false;
  }
  const currentUri = new URI(historyList[currentIndex]);
  const previousUri = new URI(previousUrl);
  const previousParams = previousUri.query(true);
  return currentUri.path() === previousUri.path() && Object.keys(currentUri.query(true)).some((name) => !(name in previousParams));
};

/**
 * Goes one step back, the same way from every back control: the dialog on top first, then the AI Helper, then the card, then the page.
 */
export const goBack = () => {
  const state = store.getState();
  const dialogName = router.selectors.dialogName(state);
  const isCardOpen = dialogName === 'CARD';
  const topDialog = topOpenedDialog();
  // The AI Helper dialog follows the URL; it is handled below.
  if (topDialog && topDialog.localName !== 'ai-chat-dialog') {
    // Disabled mid-request (sign in, verifying a connection): back waits as the close button does.
    if (topDialog.closeIconDisabled) {
      return;
    }
    // A `#hash` or a param the current entry added belongs to the top dialog only with no dialog under it (attachments Create menu), else to the one under it (AI Helper chat history).
    const ownsLayerEntry = !hasDialogUnderTop() && ((dialogName && !isCardOpen) || hasLayerHistoryEntry());
    // A dialog with a history entry (`?action`, a `#hash` dialog, or a param its opener pushed) goes back through history as before.
    if (topDialog.queryParamValue || ownsLayerEntry) {
      router.actions.back();
      return;
    }
    // One without a history entry closes as its own close / back button does; a dialog that hides that button keeps the back.
    if (topDialog._onCloseClick) {
      !topDialog.closeIconHide && topDialog._onCloseClick({});
      return;
    }
    topDialog.close();
    return;
  }

  const aiChatState = isCardOpen ? router.selectors.dialogAiChat(state) : router.selectors.pageAiChat(state);
  // The page's AI Helper pushes its own history entry; the card's replaces the card's, so it closes as its close button does.
  if (aiChatState.isOpened) {
    hasLayerHistoryEntry() ? router.actions.back() : store.dispatch(aiChat.actions.closeAiChat());
    return;
  }

  if (router.selectors.actionName(state)) {
    router.actions.back();
    return;
  }

  if (isCardOpen) {
    // A bare back() is unreliable for cross-board card stacks.
    router.actions.closeTopCardFromStack();
    return;
  }

  router.actions.back();
};

/**
 * @returns {Boolean} `true` on the first entry of the app's history with no dialog, card, action or AI Helper open.
 */
const isAtFirstPage = () => {
  const state = store.getState();
  return !history.getCurrentPageIndex() && !topOpenedDialog() && !router.selectors.dialogName(state)
    && !router.selectors.actionName(state) && !router.selectors.pageAiChat(state).isOpened;
};

/**
 * @returns {Boolean} `true` on Home with nothing open over it and nothing before it in the app's history, so a back has nowhere to go.
 */
export const isAtAppRoot = () => router.selectors.pageName(store.getState()) === 'BOARD_EXPLORER' && isAtFirstPage();

/**
 * Android's back gesture / button: the app's own back, and the app closes from its first page, whatever the page, as Android's default does.
 * Elsewhere `router.actions.back()` would fall back to Home, or throw on login / signup where there is no account.
 */
const goBackOnAndroid = () => {
  if (isAtFirstPage()) {
    navigator.app.exitApp();
    return;
  }
  goBack();
};

// While a `backbutton` listener is attached, Cordova skips its own history back.
isDeviceReady().then((isInstalledApp) => isInstalledApp && document.addEventListener('backbutton', goBackOnAndroid));
